/**
 * SiteStatusPdfPickerModal — picker for choosing PDF columns + ordering
 * + photos toggle. Wraps <ModalShell> for the chrome and close coverage;
 * uses <DragHandle> for the drag affordance on each draggable chip.
 *
 * State ownership (per spec L28):
 *   - selectedKeys + includePhotos lifted to parent via props.
 *   - onSelectionChange is the single update channel.
 *   - Group collapse state stays internal (transient UI state).
 *   - Manifest fetch stays internal (data dependency).
 *
 * On first manifest load (selectedKeys === null), the picker derives
 * defaults from the manifest and pushes them to the parent via
 * onSelectionChange. After that, parent owns the state.
 *
 * Drag-to-reorder is bounded to non-mandatory selected fields; Tool ID
 * is rendered as a standalone chip outside the SortableContext per
 * implementation Option B in spec section 7.4.
 *
 * Width math uses min_width_pts (per spec section 7.5) — the picker
 * preempts the same backend check that fires width_overflow 400.
 *
 * No localStorage, no portal, no focus trap (out of scope per spec).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ModalShell from '../../../shared/components/ModalShell';
import DragHandle from '../../../shared/components/DragHandle';
import PhotosToggle from './PhotosToggle';
import { useSiteStatusPdfManifest } from '../hooks/useSiteStatusPdfManifest';
import './SiteStatusPdfPickerModal.css';

const MODAL_TITLE = 'Configure Site Status PDF';


/* ── Small helpers ─────────────────────────────────────────────────── */

function deriveDefaults(manifest) {
  const mandatoryKey = manifest.mandatory_field.key;
  const keys = [mandatoryKey];
  for (const g of manifest.domain_groups) {
    for (const f of g.fields) {
      if (f.default_selected && f.key !== mandatoryKey) keys.push(f.key);
    }
  }
  return { keys, photos: !!manifest.photos_field.default_selected };
}

function computeInitialCollapsed(manifest) {
  const collapsed = new Set();
  for (const g of manifest.domain_groups) {
    const hasDefault = g.fields.some((f) => f.default_selected);
    if (!hasDefault) collapsed.add(g.label);
  }
  return collapsed;
}


/* ── Sub-components ────────────────────────────────────────────────── */

function Chevron({ open }) {
  return (
    <span className={`sspm-chevron${open ? ' sspm-chevron--open' : ''}`} aria-hidden="true">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function MandatoryChip({ label }) {
  return (
    <div className="sspm-chip sspm-chip--mandatory" title="Tool ID is required and cannot be removed">
      <span className="sspm-chip-label">{label}</span>
    </div>
  );
}

function SortableChip({ id, label, onRemove, onMouseEnter, onMouseLeave, highlighted }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const className = `sspm-chip${highlighted ? ' sspm-chip--highlighted' : ''}${isDragging ? ' sspm-chip--dragging' : ''}`;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={className}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <DragHandle attributes={attributes} listeners={listeners} ariaLabel={`Reorder ${label}`} />
      <span className="sspm-chip-label">{label}</span>
      <button
        type="button"
        className="sspm-chip-remove"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
      >
        &times;
      </button>
    </div>
  );
}

function FieldRow({ field, isSelected, isMandatory, disabledByCap, highlighted, onToggle, onMouseEnter, onMouseLeave }) {
  const disabled = isMandatory || (!isSelected && disabledByCap);
  let title;
  if (isMandatory) title = 'Tool ID is required and cannot be removed';
  else if (!isSelected && disabledByCap) title = 'Maximum 13 data columns selected. Remove a column to add another.';

  const labelEl = (
    <label
      className={`sspm-row${highlighted ? ' sspm-row--highlighted' : ''}${disabled ? ' sspm-row--disabled' : ''}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <input
        type="checkbox"
        className="sspm-row-check"
        checked={isSelected}
        disabled={disabled}
        onChange={onToggle}
      />
      <span className="sspm-row-label">{field.label}</span>
    </label>
  );

  // Disabled <input>s don't emit mouseover events in most browsers, so a
  // `title` attribute on the input itself is invisible. Wrap the row in
  // a <span title> when a tooltip is needed; the span is what actually
  // receives the events. (Phase 5 closing doc §4.5.)
  if (title) {
    return (
      <span className="sspm-row-tooltip-wrapper" title={title}>
        {labelEl}
      </span>
    );
  }
  return labelEl;
}


/* ── Main component ────────────────────────────────────────────────── */

export default function SiteStatusPdfPickerModal({
  isOpen,
  onClose,
  onConfirm,
  selectedKeys,
  includePhotos,
  onSelectionChange,
}) {
  const { manifest, loading, error, refetch } = useSiteStatusPdfManifest();

  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [hoveredKey, setHoveredKey] = useState(null);

  /* ── Derived ─────────────────────────────────────────────────────── */

  const fieldsByKey = useMemo(() => {
    const map = new Map();
    if (!manifest) return map;
    for (const g of manifest.domain_groups) {
      for (const f of g.fields) map.set(f.key, f);
    }
    return map;
  }, [manifest]);

  const mandatoryKey = manifest?.mandatory_field?.key ?? null;
  const maxDataColumns = manifest?.max_data_columns ?? 13;
  const widthBudget = manifest?.usable_width_pts ?? 720;
  const photosWidth = manifest?.photos_field?.preferred_width_pts ?? 60;

  const selectedSet = useMemo(() => new Set(selectedKeys || []), [selectedKeys]);

  const dataColumnCount = Math.max(0, (selectedKeys?.length ?? 1) - 1);
  const isAtCap = dataColumnCount >= maxDataColumns;

  const widthSum = useMemo(() => {
    if (!manifest || !selectedKeys) return 0;
    let sum = 0;
    for (const key of selectedKeys) {
      const f = fieldsByKey.get(key);
      if (f) sum += f.min_width_pts ?? 0;
    }
    if (includePhotos) sum += photosWidth;
    return sum;
  }, [selectedKeys, includePhotos, fieldsByKey, manifest, photosWidth]);

  const fits = widthSum <= widthBudget;
  const overByPts = Math.max(0, widthSum - widthBudget);

  const ready = !!manifest && !loading && !error;
  const canGenerate = ready && Array.isArray(selectedKeys) && selectedKeys.length >= 1
    && dataColumnCount <= maxDataColumns && fits;

  /* ── Default-selection initialization ────────────────────────────── */

  useEffect(() => {
    if (!manifest) return;
    if (selectedKeys !== null && selectedKeys !== undefined) return;
    const { keys, photos } = deriveDefaults(manifest);
    onSelectionChange(keys, photos);
  }, [manifest, selectedKeys, onSelectionChange]);

  /* ── Initialize collapse state from manifest defaults ────────────── */

  useEffect(() => {
    if (manifest) setCollapsedGroups(computeInitialCollapsed(manifest));
  }, [manifest]);

  /* ── Selection mutators ──────────────────────────────────────────── */

  const handleToggleField = useCallback((key) => {
    if (!manifest) return;
    if (key === mandatoryKey) return;
    const isSelected = selectedSet.has(key);
    let next;
    if (isSelected) {
      next = (selectedKeys || []).filter((k) => k !== key);
    } else {
      if (isAtCap) return;
      next = [...(selectedKeys || [mandatoryKey]), key];
    }
    onSelectionChange(next, !!includePhotos);
  }, [manifest, mandatoryKey, selectedSet, selectedKeys, isAtCap, includePhotos, onSelectionChange]);

  const handleRemoveChip = useCallback((key) => {
    if (key === mandatoryKey) return;
    const next = (selectedKeys || []).filter((k) => k !== key);
    onSelectionChange(next, !!includePhotos);
  }, [mandatoryKey, selectedKeys, includePhotos, onSelectionChange]);

  const handleToggleAllInGroup = useCallback((group, selectAll) => {
    if (!manifest) return;
    const groupKeys = group.fields.map((f) => f.key).filter((k) => k !== mandatoryKey);
    let next;
    if (selectAll) {
      const remaining = maxDataColumns - dataColumnCount;
      const toAdd = groupKeys.filter((k) => !selectedSet.has(k)).slice(0, Math.max(0, remaining));
      next = [...(selectedKeys || [mandatoryKey]), ...toAdd];
    } else {
      const groupSet = new Set(groupKeys);
      next = (selectedKeys || []).filter((k) => !groupSet.has(k));
    }
    onSelectionChange(next, !!includePhotos);
  }, [manifest, mandatoryKey, maxDataColumns, dataColumnCount, selectedSet, selectedKeys, includePhotos, onSelectionChange]);

  const handlePhotosChange = useCallback((next) => {
    onSelectionChange(selectedKeys || [mandatoryKey], next);
  }, [selectedKeys, mandatoryKey, onSelectionChange]);

  const handleReset = useCallback(() => {
    if (!manifest) return;
    const { keys, photos } = deriveDefaults(manifest);
    onSelectionChange(keys, photos);
    setCollapsedGroups(computeInitialCollapsed(manifest));
  }, [manifest, onSelectionChange]);

  /* ── Group collapse ──────────────────────────────────────────────── */

  const handleToggleCollapse = useCallback((groupLabel) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupLabel)) next.delete(groupLabel);
      else next.add(groupLabel);
      return next;
    });
  }, []);

  /* ── Drag-to-reorder ─────────────────────────────────────────────── */

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (!Array.isArray(selectedKeys)) return;
    const nonMandatory = selectedKeys.filter((k) => k !== mandatoryKey);
    const oldIdx = nonMandatory.indexOf(active.id);
    const newIdx = nonMandatory.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(nonMandatory, oldIdx, newIdx);
    onSelectionChange([mandatoryKey, ...reordered], !!includePhotos);
  }, [selectedKeys, mandatoryKey, includePhotos, onSelectionChange]);

  /* ── Render: footer ──────────────────────────────────────────────── */

  const footer = ready && (
    <div className="sspm-footer">
      <button
        type="button"
        className="sspm-btn sspm-btn--ghost"
        onClick={handleReset}
      >
        Reset to defaults
      </button>
      <div className="sspm-footer-actions">
        <button
          type="button"
          className="sspm-btn sspm-btn--secondary"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="sspm-btn sspm-btn--primary"
          disabled={!canGenerate}
          onClick={() => onConfirm(selectedKeys, !!includePhotos)}
        >
          Generate
        </button>
      </div>
    </div>
  );

  /* ── Render: body ────────────────────────────────────────────────── */

  let bodyContent;
  if (loading) {
    bodyContent = <p className="sspm-status">Loading field options...</p>;
  } else if (error) {
    bodyContent = (
      <div className="sspm-status sspm-status--error">
        <p>{error}</p>
        <button type="button" className="sspm-btn sspm-btn--secondary" onClick={refetch}>
          Retry
        </button>
      </div>
    );
  } else if (manifest && Array.isArray(selectedKeys)) {
    const nonMandatorySelected = selectedKeys.filter((k) => k !== mandatoryKey);

    bodyContent = (
      <div className="sspm-body">
        {/* ── Selected-fields strip ───────────────────────── */}
        <div className="sspm-strip" aria-label="Selected fields in display order">
          <MandatoryChip label={manifest.mandatory_field.label} />
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={nonMandatorySelected} strategy={horizontalListSortingStrategy}>
              {nonMandatorySelected.map((key) => {
                const f = fieldsByKey.get(key);
                if (!f) return null;
                return (
                  <SortableChip
                    key={key}
                    id={key}
                    label={f.label}
                    highlighted={hoveredKey === key}
                    onMouseEnter={() => setHoveredKey(key)}
                    onMouseLeave={() => setHoveredKey(null)}
                    onRemove={() => handleRemoveChip(key)}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </div>

        {/* ── Count + width indicators ────────────────────── */}
        <div className="sspm-indicators">
          <span className="sspm-indicator-count">
            {dataColumnCount} of {maxDataColumns} data columns selected
          </span>
          <span className={`sspm-indicator-width sspm-indicator-width--${fits ? 'ok' : 'over'}`}>
            {fits
              ? 'Selection fits page'
              : `Selection too wide — ${overByPts} pt over budget`}
          </span>
        </div>

        {/* ── Photos toggle (outside groups) ──────────────── */}
        <div className="sspm-photos-row">
          <PhotosToggle value={!!includePhotos} onChange={handlePhotosChange} />
        </div>

        {/* ── Domain groups list ──────────────────────────── */}
        <div className="sspm-groups">
          {manifest.domain_groups.map((group) => {
            const isCollapsed = collapsedGroups.has(group.label);
            const groupSelectableKeys = group.fields
              .map((f) => f.key)
              .filter((k) => k !== mandatoryKey);
            const allGroupSelected = groupSelectableKeys.length > 0
              && groupSelectableKeys.every((k) => selectedSet.has(k));

            return (
              <div key={group.label} className="sspm-group">
                <div className="sspm-group-header">
                  <button
                    type="button"
                    className="sspm-group-toggle"
                    onClick={() => handleToggleCollapse(group.label)}
                    aria-expanded={!isCollapsed}
                  >
                    <Chevron open={!isCollapsed} />
                    <span className="sspm-group-label">{group.label}</span>
                  </button>
                  {groupSelectableKeys.length > 0 && (
                    <button
                      type="button"
                      className="sspm-group-select-all"
                      onClick={() => handleToggleAllInGroup(group, !allGroupSelected)}
                    >
                      {allGroupSelected ? 'Deselect all' : 'Select all'}
                    </button>
                  )}
                </div>
                {!isCollapsed && (
                  <div className="sspm-group-rows">
                    {group.fields.map((f) => {
                      const isMandatory = f.key === mandatoryKey;
                      const isSelected = selectedSet.has(f.key) || isMandatory;
                      return (
                        <FieldRow
                          key={f.key}
                          field={f}
                          isSelected={isSelected}
                          isMandatory={isMandatory}
                          disabledByCap={isAtCap}
                          highlighted={hoveredKey === f.key}
                          onToggle={() => handleToggleField(f.key)}
                          onMouseEnter={() => setHoveredKey(f.key)}
                          onMouseLeave={() => setHoveredKey(null)}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  } else {
    // Manifest loaded but selectedKeys still being initialized (between
    // the default-deriving useEffect and the parent's re-render). Brief
    // loading-equivalent state — usually flashes for one render at most.
    bodyContent = <p className="sspm-status">Loading field options...</p>;
  }

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={MODAL_TITLE}
      footer={footer}
    >
      {bodyContent}
    </ModalShell>
  );
}
